import { useState, useEffect } from 'react';
import { Navbar } from '@/components/layout/Navbar';
import { InstanceFooter } from '@/components/layout/InstanceFooter';
import { commentApi, RecentComment } from '@/services/api';
import { renderCommentContent } from '@/components/comments/CommentItem';
import { ChatBubbleLeftIcon } from '@heroicons/react/24/outline';
import { useAppStore } from '@/stores/appStore';
import {
  productApi,
  subProductApi,
  initiativeApi,
  statusApi,
  fiscalYearApi,
  labsApi,
} from '@/services/api';

const PAGE_SIZE = 50;

function formatDateTime(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

export function CommentsLogPage() {
  const [comments, setComments] = useState<RecentComment[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [total, setTotal] = useState(0);
  const {
    initiatives,
    setProducts,
    setInitiatives,
    setStatuses,
    setQuarters,
    setLabsFeatures,
    openViewInitiativeModal,
  } = useAppStore();

  // Load reference data
  useEffect(() => {
    const loadData = async () => {
      try {
        const [
          productsData,
          subProductsData,
          initiativesData,
          statusesData,
          fiscalYearsData,
          labsData,
        ] = await Promise.all([
          productApi.getAll(),
          subProductApi.getAll(),
          initiativeApi.getAll(),
          statusApi.getAll(),
          fiscalYearApi.getAll(),
          labsApi.getAll().catch(() => []),
        ]);

        const productsWithSubProducts = productsData.map((product: any) => ({
          ...product,
          subProducts: subProductsData.filter(
            (sp: any) => sp.productId === product.id
          ),
        }));

        setProducts(productsWithSubProducts);
        setInitiatives(initiativesData);
        setStatuses(statusesData);

        const allQuarters = fiscalYearsData
          .flatMap((fy: any) => fy.quarters || [])
          .sort((a: any, b: any) => a.displayOrder - b.displayOrder);
        setQuarters(allQuarters);
        if (labsData.length > 0) setLabsFeatures(labsData);
      } catch (error) {
        console.error('Failed to load reference data:', error);
      }
    };

    loadData();
  }, [setProducts, setInitiatives, setStatuses, setQuarters, setLabsFeatures]);

  // Fetch initial comments
  useEffect(() => {
    const fetchComments = async () => {
      setLoading(true);
      try {
        const response = await commentApi.getRecent(PAGE_SIZE, 0);
        setComments(response.data);
        setHasMore(response.hasMore);
        setTotal(response.total);
      } catch (error) {
        console.error('Failed to fetch comments:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchComments();
  }, []);

  // Load more comments
  const handleLoadMore = async () => {
    setLoadingMore(true);
    try {
      const response = await commentApi.getRecent(PAGE_SIZE, comments.length);
      setComments((prev) => [...prev, ...response.data]);
      setHasMore(response.hasMore);
    } catch (error) {
      console.error('Failed to load more comments:', error);
    } finally {
      setLoadingMore(false);
    }
  };

  const handleCommentClick = (initiativeId: string) => {
    const initiative = initiatives.find((i) => i.id === initiativeId);
    if (initiative) {
      openViewInitiativeModal(initiative, 'comments');
    }
  };

  // Group comments by date
  const groupedByDate = comments.reduce((acc, comment) => {
    const date = new Date(comment.createdAt).toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    });
    if (!acc[date]) {
      acc[date] = [];
    }
    acc[date].push(comment);
    return acc;
  }, {} as Record<string, RecentComment[]>);

  return (
    <div className="flex flex-col h-screen bg-gray-50">
      <Navbar />

      <main className="flex-1 overflow-auto">
        <div className="max-w-4xl mx-auto px-6 py-6">
          <h1 className="text-2xl font-bold text-gray-900 mb-6">Comments Log</h1>

          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : comments.length === 0 ? (
            <div className="text-center py-12">
              <ChatBubbleLeftIcon className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500">No comments yet</p>
            </div>
          ) : (
            <>
              {/* Comment count */}
              <div className="text-sm text-gray-500 mb-4">
                Showing {comments.length} of {total} comments
              </div>

              <div className="space-y-8">
                {Object.entries(groupedByDate).map(([date, dateComments]) => (
                  <div key={date}>
                    <h2 className="text-sm font-medium text-gray-500 mb-3">{date}</h2>
                    <div className="space-y-3">
                      {dateComments.map((comment) => {
                        const isReply = !!comment.parentId;

                        return (
                          <button
                            key={comment.id}
                            onClick={() => handleCommentClick(comment.initiative.id)}
                            className="w-full text-left bg-white rounded-lg shadow-sm p-4 hover:shadow-md transition-all"
                          >
                            <div className="flex items-start gap-3">
                              <ChatBubbleLeftIcon className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center justify-between gap-2 mb-1">
                                  <span className="font-medium text-gray-900 truncate">
                                    {comment.initiative.title}
                                  </span>
                                  <span className="text-xs text-gray-400 flex-shrink-0">
                                    {formatDateTime(comment.createdAt)}
                                  </span>
                                </div>
                                <div
                                  className="text-sm text-gray-500 mb-2 truncate"
                                  title={`${comment.initiative.subProduct.product.name} > ${comment.initiative.subProduct.name}`}
                                >
                                  {comment.initiative.subProduct.product.name} &gt;{' '}
                                  {comment.initiative.subProduct.name}
                                </div>
                                <div className="text-sm text-gray-700 mb-2">
                                  {isReply && <span className="text-gray-400">↳ </span>}
                                  "{renderCommentContent(comment.content)}"
                                </div>
                                <div className="text-xs text-gray-500">
                                  {comment.user.name}
                                </div>
                              </div>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>

              {/* Load More button */}
              {hasMore && (
                <div className="mt-8 text-center">
                  <button
                    onClick={handleLoadMore}
                    disabled={loadingMore}
                    className="px-6 py-2 bg-white border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {loadingMore ? (
                      <span className="flex items-center gap-2">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-600"></div>
                        Loading...
                      </span>
                    ) : (
                      'Load More'
                    )}
                  </button>
                </div>
              )}

              {/* End of list message */}
              {!hasMore && comments.length > 0 && (
                <div className="mt-8 text-center text-sm text-gray-400">
                  No more comments
                </div>
              )}
            </>
          )}
        </div>
      </main>

      <InstanceFooter />
    </div>
  );
}
